import React, { useState, useEffect } from 'react';
import { useInventoryPlanning } from '../contexts/InventoryPlanningContext';

type GenerateParams = {
  strategy: 'coverage' | 'ss_lt';
  service_z: number; // UI uses niveau de service (0.90..0.99). Backend accepts level or z.
  container_sizes_m3: Array<30 | 65>;
  min_fill_rate: number;
  // New advanced params
  rop_mode?: 'rop_ss' | 'rop_cb';
  pull_forward_window_months?: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (params: GenerateParams) => void;
};

const OrderParamsModal: React.FC<Props> = ({ open, onClose, onConfirm }) => {
  const { planningMode, allowNegativeStock } = useInventoryPlanning();
  // targetCoverage removed (global CB coverage handled in header)
  const [serviceZ] = useState<number>(0.95);
  const [container30, setContainer30] = useState<boolean>(false);
  const [container65, setContainer65] = useState<boolean>(true);
  const [minFill, setMinFill] = useState<number>(0.9);
  // Advanced parameters
  // coverageMonths removed from modal (global in header)
  // holding_cost_per_unit_month removed (not used in backend computation)
  const [pullForward, setPullForward] = useState<number>(1);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow-lg w-full max-w-lg">
        <div className="p-4 border-b font-medium">Paramètres de génération</div>
        <div className="p-4 space-y-4 text-sm">
          {/* No CB coverage here: handled globally in l'en-tête */}
          <div>
            <label className="block mb-1 font-medium" title="Types de conteneurs utilisables pour la consolidation.">Tailles de conteneur autorisées</label>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={container30} onChange={(e) => setContainer30(e.target.checked)} /> 30 m³
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={container65} onChange={(e) => setContainer65(e.target.checked)} /> 65 m³
              </label>
            </div>
            <div className="text-xs text-gray-500 mt-1">Sélectionnez au moins un type.</div>
          </div>
          <div>
            <label className="block mb-1 font-medium" title="Pourcentage minimal de remplissage visé par conteneur (0 à 1).">Taux de remplissage minimum</label>
            <input type="number" step={0.05} min={0} max={1} className="border rounded px-2 py-1 w-24" value={minFill} onChange={(e) => setMinFill(Number(e.target.value || 0))} />
          </div>
          <div className="border-t pt-3">
            <div className="font-medium mb-2">Paramètres avancés</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-1" title="Logique de point de commande: SS = stock de sécurité, CB = buffer de couverture.">Mode de planification (ROP)</label>
                <div className="border rounded px-2 py-1 w-full bg-gray-50 text-gray-700">
                  {planningMode === 'ss' ? 'Stock sécurité (SS)' : 'Couverture (CB)'}
                </div>
              </div>
              <div>
                <label className="block mb-1" title="Autoriser les inventaires projetés à être négatifs (ventes sur stock non encore reçu).">Stock négatif autorisé</label>
                <div className="border rounded px-2 py-1 w-full bg-gray-50 text-gray-700">
                  {allowNegativeStock ? 'Oui' : 'Non'}
                </div>
              </div>
              {/* Couverture (mois, CB) supprimée du modal: globale en header */}
              {/* holding_cost_per_unit_month removed: not used in backend computation */}
              {/* Pull-forward only shown in SS mode (not used in CB mode) */}
              {planningMode === 'ss' && (
                <div>
                  <label htmlFor="pull-forward-input" className="block mb-1" title="Fenêtre d'anticipation (mois) lors de la consolidation (priorité faible). Maximum: 11 mois (limité par le plan 12 mois).">Anticipation des besoins (mois)</label>
                  <input id="pull-forward-input" type="number" className="border rounded px-2 py-1 w-full" value={pullForward} min={0} max={11} onChange={(e) => {
                    const val = Number(e.target.value || 0);
                    setPullForward(Math.max(0, Math.min(11, val)));
                  }} />
                </div>
              )}
              {/* Pénalités solver retirées en mode gréedy */}
            </div>
            <div className="text-xs text-gray-500 mt-1">Laissez les valeurs par défaut si vous n'êtes pas sûr.</div>
          </div>
        </div>
        <div className="p-3 border-t flex justify-end gap-2">
          <button className="px-3 py-2 text-sm" onClick={onClose}>Annuler</button>
          <button
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={!container30 && !container65}
            onClick={() => {
              const sizes: Array<30 | 65> = [];
              if (container30) sizes.push(30);
              if (container65) sizes.push(65);
              const confirmParams: GenerateParams = {
                strategy: planningMode === 'cb' ? 'coverage' : 'ss_lt',
                service_z: serviceZ,
                container_sizes_m3: sizes,
                min_fill_rate: minFill,
                rop_mode: planningMode === 'ss' ? 'rop_ss' : 'rop_cb',
                // Only include pull_forward_window_months in SS mode (not used in CB mode)
                ...(planningMode === 'ss' && { pull_forward_window_months: pullForward }),
                // backend hint for negative stock policy
                // snake_case to match backend conventions
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                allow_negative_stock: allowNegativeStock,
              };
              onConfirm(confirmParams);
            }}
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderParamsModal;


